import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { fetchSessionProfile } from "./authApi";
import { extractMemberIdentity, getCsrfCookieDiagnostic } from "./sessionHelpers";
import { login, logout, redirectToLogin } from "./authGuards";

/**
 * @typedef {'idle'|'loading'|'authenticated'|'unauthenticated'|'error'|'expired'|'unconfigured'} AuthStatus
 */

const ClassicSiteAuthContext = createContext(null);

const initialState = {
  status: "idle",
  profile: null,
  error: null,
  httpStatus: null,
  lastCheckedAt: null,
  csrfPresent: false,
  csrfDiagnostic: null,
  lastDiagnostic: null,
};

export function ClassicSiteAuthProvider({ children }) {
  const [state, setState] = useState(initialState);
  const wasAuthenticatedRef = useRef(false);

  const applySessionResult = useCallback((result) => {
    const isAuthenticated = result.status === "authenticated";
    const csrfDiagnostic = getCsrfCookieDiagnostic({ isAuthenticated });

    if (result.status === "unconfigured") {
      setState({
        status: "unconfigured",
        profile: null,
        error: result.message,
        httpStatus: null,
        lastCheckedAt: Date.now(),
        csrfPresent: csrfDiagnostic.readableInJavaScript,
        csrfDiagnostic,
        lastDiagnostic: result.diagnostic || null,
      });
      return;
    }

    if (result.status === "authenticated") {
      wasAuthenticatedRef.current = true;
      setState({
        status: "authenticated",
        profile: result.profile,
        error: null,
        httpStatus: result.httpStatus,
        lastCheckedAt: Date.now(),
        csrfPresent: csrfDiagnostic.readableInJavaScript,
        csrfDiagnostic,
        lastDiagnostic: result.diagnostic || null,
      });
      return;
    }

    if (result.status === "unauthenticated") {
      const nextStatus =
        wasAuthenticatedRef.current ? "expired" : "unauthenticated";
      setState({
        status: nextStatus,
        profile: null,
        error: result.message,
        httpStatus: result.httpStatus,
        lastCheckedAt: Date.now(),
        csrfPresent: csrfDiagnostic.readableInJavaScript,
        csrfDiagnostic,
        lastDiagnostic: result.diagnostic || null,
      });
      return;
    }

    setState({
      status: "error",
      profile: null,
      error: result.message,
      httpStatus: result.httpStatus,
      lastCheckedAt: Date.now(),
      csrfPresent: csrfDiagnostic.readableInJavaScript,
      csrfDiagnostic,
      lastDiagnostic: result.diagnostic || null,
    });
  }, []);

  const refresh = useCallback(async () => {
    setState((prev) => ({
      ...prev,
      status: "loading",
      error: null,
    }));
    const result = await fetchSessionProfile();
    applySessionResult(result);
    return result;
  }, [applySessionResult]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        refresh();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  const identity = useMemo(
    () => (state.profile ? extractMemberIdentity(state.profile) : null),
    [state.profile]
  );

  const value = useMemo(
    () => ({
      ...state,
      isLoading: state.status === "idle" || state.status === "loading",
      isAuthenticated: state.status === "authenticated",
      identity,
      user: state.profile?.user || null,
      hoodie: state.profile
        ? {
            id: state.profile.id,
            zipCode: state.profile.zip_code,
            gravatarUrl: state.profile.gravatar_url,
            recommendations: state.profile.recommendations,
          }
        : null,
      refresh,
      login,
      logout,
      redirectToLogin,
    }),
    [state, identity, refresh]
  );

  return (
    <ClassicSiteAuthContext.Provider value={value}>
      {children}
    </ClassicSiteAuthContext.Provider>
  );
}

export function useClassicSiteAuth() {
  const ctx = useContext(ClassicSiteAuthContext);
  if (!ctx) {
    throw new Error("useClassicSiteAuth must be used within ClassicSiteAuthProvider");
  }
  return ctx;
}

export { ClassicSiteAuthContext };
